import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  Box, 
  Button, 
  Menu, 
  MenuItem, 
  Typography,
  Divider,
  Avatar
} from '@mui/material';
import { Container } from 'Components/Container';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { getExportFileRopa, getRopaById } from 'services/report-ropa-service';
import { ReportsRopaProcess } from './Process';
import { ReportsRopaArea } from './Area';
import { useReactToPrint } from 'react-to-print';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined';
import { ReportsRopaCompany } from './Company';
import { useForm } from 'react-hook-form';
import { green, red } from '@mui/material/colors';
import { motion } from 'framer-motion';
import { ReportsRopaExecutiveSummary } from './ExecutiveSummary';
import { FILTER } from './filter-types';

export const ReportsRopaShow = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { id } = useParams();
  const componentRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { watch } = useForm({
    defaultValues: {
      filter: '',
      area: '',
      process: '',
      data: '',
      category: '',
      processing: '',
      legalBase: '',
      position: '',
    },
  });

  const {
    isLoading,
    error,
    data: ropa,
  } = useQuery('getReportRopaById', async () => {
    const dataRopa = await getRopaById(id);
    if (dataRopa.status === 200) {
      return dataRopa.data;
    }
    return null;
  });

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: '@page { size: 10in 12in }',
  });

  const getContentFiltered = () => {
    const contentRopa = {
      name: ropa.name,
      scope: ropa.scope,
      company: {},
      area: {},
      process: {},
    };
    if (ropa.scope === 'Empresa') {
      contentRopa.company.name = ropa.company.name;
      contentRopa.company.description = ropa.company.description;
      contentRopa.company.areas = [];

      ropa.company.areas.forEach(itemArea => {
        const contentArea = {
          name: itemArea.name,
          description: itemArea.description,
          user: itemArea.user,
          processes: [],
        };
        itemArea.processes.forEach(itemProcess => {
          const contentProcess = {
            name: itemProcess.name,
            descriptionDataFlow: itemProcess.descriptionDataFlow,
            user: itemProcess.user,
            dataMapping: [],
          };
          itemProcess.dataMapping.forEach(itemDataMap => {
            const contentDataMap = {
              name: itemDataMap.name,
              category: itemDataMap.category,
              dataSource: itemDataMap.dataSource,
              holderProfile: itemDataMap.holderProfile,
              retentionPeriod: itemDataMap.retentionPeriod,
              storage: itemDataMap.storage,
              dataProcessing: [],
            };
            itemDataMap.dataProcessing.forEach(itemDataProcess => {
              const contentDataProcess = {
                complementLegalBasis: itemDataProcess.complementLegalBasis,
                destiny: itemDataProcess.destiny,
                finality: itemDataProcess.finality,
                legalBase: itemDataProcess.legalBase,
                observation: itemDataProcess.observation,
                position: itemDataProcess.position,
                processing: itemDataProcess.processing,
                shareType: itemDataProcess.shareType,
                tool: itemDataProcess.tool,
              };
              contentDataMap.dataProcessing.push(contentDataProcess);
            });
            contentProcess.dataMapping.push(contentDataMap);
          });
          contentArea.processes.push(contentProcess);
        });
        contentRopa.company.areas.push(contentArea);
      });

      contentRopa.company.areas = contentRopa.company.areas.filter(areaFilter => {
        if (watch('filter') === FILTER.AREA && !!watch('area')) {
          return areaFilter.name === watch('area');
        }
        if (watch('filter') === FILTER.PROCESS && !!watch('process')) {
          return areaFilter.processes.some(itemProcess => itemProcess.name === watch('process'));
        }
        if (watch('filter') === FILTER.DATA && !!watch('data')) {
          return areaFilter.processes.some(itemProcess => {
            return itemProcess.dataMapping.some(dataMap => dataMap.name === watch('data'));
          });
        }
        if (watch('filter') === FILTER.CATEGORY && !!watch('category')) {
          return areaFilter.processes.some(itemProcess => {
            return itemProcess.dataMapping.some(dataMap => dataMap.category === watch('category'));
          });
        }
        if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
          return areaFilter.processes.some(itemProcess => {
            return itemProcess.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.processing === watch('processing');
              });
            });
          });
        }
        if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
          return areaFilter.processes.some(itemProcess => {
            return itemProcess.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.legalBase === watch('legalBase');
              });
            });
          });
        }
        if (watch('filter') === FILTER.POSITION && !!watch('position')) {
          return areaFilter.processes.some(itemProcess => {
            return itemProcess.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.position === watch('position');
              });
            });
          });
        }
        return true;
      });

      contentRopa.company.areas.forEach(areaFilter => {
        areaFilter.processes = areaFilter.processes.filter(processFilter => {
          if (watch('filter') === FILTER.PROCESS && !!watch('process')) {
            return processFilter.name === watch('process');
          }
          if (watch('filter') === FILTER.DATA && !!watch('data')) {
            return processFilter.dataMapping.some(dataMap => dataMap.name === watch('data'));
          }
          if (watch('filter') === FILTER.CATEGORY && !!watch('category')) {
            return processFilter.dataMapping.some(dataMap => dataMap.category === watch('category'));
          }
          if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
            return processFilter.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.processing === watch('processing');
              });
            });
          }
          if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
            return processFilter.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.legalBase === watch('legalBase');
              });
            });
          }
          if (watch('filter') === FILTER.POSITION && !!watch('position')) {
            return processFilter.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.position === watch('position');
              });
            });
          }
          return true;
        });

        areaFilter.processes.forEach(processFilter => {
          processFilter.dataMapping = processFilter.dataMapping.filter(dataMap => {
            if (watch('filter') === FILTER.DATA && !!watch('data')) {
              return dataMap.name === watch('data');
            }
            if (watch('filter') === FILTER.CATEGORY && !!watch('category')) {
              return dataMap.category === watch('category');
            }
            if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
              return dataMap.dataProcessing.some(itemDataProcess => {
                return itemDataProcess.processing === watch('processing');
              });
            }
            if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
              return dataMap.dataProcessing.some(itemDataProcess => {
                return itemDataProcess.legalBase === watch('legalBase');
              });
            }
            if (watch('filter') === FILTER.POSITION && !!watch('position')) {
              return dataMap.dataProcessing.some(itemDataProcess => {
                return itemDataProcess.position === watch('position');
              });
            }
            return true;
          });

          processFilter.dataMapping.forEach(dataMap => {
            dataMap.dataProcessing = dataMap.dataProcessing.filter(dataProcess => {
              if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
                return dataProcess.processing === watch('processing');
              }
              if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
                return dataProcess.legalBase === watch('legalBase');
              }
              if (watch('filter') === FILTER.POSITION && !!watch('position')) {
                return dataProcess.position === watch('position');
              }
              return true;
            });
          });
        });
      });
    } else if (ropa.scope === 'Área') {
      contentRopa.area.name = ropa.area.name;
      contentRopa.area.description = ropa.area.description;
      contentRopa.area.user = ropa.area.user;
      contentRopa.area.processes = [];

      ropa.area.processes.forEach(itemProcess => {
        const contentProcess = {
          name: itemProcess.name,
          descriptionDataFlow: itemProcess.descriptionDataFlow,
          user: itemProcess.user,
          dataMapping: [],
        };
        itemProcess.dataMapping.forEach(itemDataMap => {
          const contentDataMap = {
            name: itemDataMap.name,
            category: itemDataMap.category,
            dataSource: itemDataMap.dataSource,
            holderProfile: itemDataMap.holderProfile,
            retentionPeriod: itemDataMap.retentionPeriod,
            storage: itemDataMap.storage,
            dataProcessing: [],
          };
          itemDataMap.dataProcessing.forEach(itemDataProcess => {
            const contentDataProcess = {
              complementLegalBasis: itemDataProcess.complementLegalBasis,
              destiny: itemDataProcess.destiny,
              finality: itemDataProcess.finality,
              legalBase: itemDataProcess.legalBase,
              observation: itemDataProcess.observation,
              position: itemDataProcess.position,
              processing: itemDataProcess.processing,
              shareType: itemDataProcess.shareType,
              tool: itemDataProcess.tool,
            };
            contentDataMap.dataProcessing.push(contentDataProcess);
          });
          contentProcess.dataMapping.push(contentDataMap);
        });
        contentRopa.area.processes.push(contentProcess);
      });

      contentRopa.area.processes = contentRopa.area.processes
        .filter(processFilter => {
          if (watch('filter') === FILTER.PROCESS && !!watch('process')) {
            return processFilter.name === watch('process');
          }
          if (watch('filter') === FILTER.DATA && !!watch('data')) {
            return processFilter.dataMapping.some(dataMap => dataMap.name === watch('data'));
          }
          if (watch('filter') === FILTER.CATEGORY && !!watch('category')) {
            return processFilter.dataMapping.some(dataMap => dataMap.category === watch('category'));
          }
          if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
            return processFilter.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.processing === watch('processing');
              });
            });
          }
          if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
            return processFilter.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.legalBase === watch('legalBase');
              });
            });
          }
          if (watch('filter') === FILTER.POSITION && !!watch('position')) {
            return processFilter.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.position === watch('position');
              });
            });
          }
          return true;
        })
        .map(processFilter => {
          processFilter.dataMapping = processFilter.dataMapping
            .filter(dataMap => {
              if (watch('filter') === FILTER.DATA && !!watch('data')) {
                return dataMap.name === watch('data');
              }
              if (watch('filter') === FILTER.CATEGORY && !!watch('category')) {
                return dataMap.category === watch('category');
              }
              if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
                return dataMap.dataProcessing.some(itemDataProcess => {
                  return itemDataProcess.processing === watch('processing');
                });
              }
              if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
                return dataMap.dataProcessing.some(itemDataProcess => {
                  return itemDataProcess.legalBase === watch('legalBase');
                });
              }
              if (watch('filter') === FILTER.POSITION && !!watch('position')) {
                return dataMap.dataProcessing.some(itemDataProcess => {
                  return itemDataProcess.position === watch('position');
                });
              }
              return true;
            })
            .map(dataMap => {
              dataMap.dataProcessing = dataMap.dataProcessing.filter(dataProcess => {
                if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
                  return dataProcess.processing === watch('processing');
                }
                if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
                  return dataProcess.legalBase === watch('legalBase');
                }
                if (watch('filter') === FILTER.POSITION && !!watch('position')) {
                  return dataProcess.position === watch('position');
                }
                return true;
              });
              return dataMap;
            });
          return processFilter;
        });
    } else if (ropa.scope === 'Processo') {
      contentRopa.process.name = ropa.process.name;
      contentRopa.process.descriptionDataFlow = ropa.process.descriptionDataFlow;
      contentRopa.process.user = ropa.process.user;
      contentRopa.process.dataMapping = [];

      ropa.process.dataMapping.forEach(itemDataMap => {
        const contentDataMap = {
          name: itemDataMap.name,
          category: itemDataMap.category,
          dataSource: itemDataMap.dataSource,
          holderProfile: itemDataMap.holderProfile,
          retentionPeriod: itemDataMap.retentionPeriod,
          storage: itemDataMap.storage,
          dataProcessing: [],
        };
        itemDataMap.dataProcessing.forEach(itemDataProcess => {
          const contentDataProcess = {
            complementLegalBasis: itemDataProcess.complementLegalBasis,
            destiny: itemDataProcess.destiny,
            finality: itemDataProcess.finality,
            legalBase: itemDataProcess.legalBase,
            observation: itemDataProcess.observation,
            position: itemDataProcess.position,
            processing: itemDataProcess.processing,
            shareType: itemDataProcess.shareType,
            tool: itemDataProcess.tool,
          };
          contentDataMap.dataProcessing.push(contentDataProcess);
        });
        contentRopa.process.dataMapping.push(contentDataMap);
      });

      contentRopa.process.dataMapping = contentRopa.process.dataMapping
        .filter(dataMap => {
          if (watch('filter') === FILTER.DATA && !!watch('data')) {
            return dataMap.name === watch('data');
          }
          if (watch('filter') === FILTER.CATEGORY && !!watch('category')) {
            return dataMap.category === watch('category');
          }
          if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
            return dataMap.dataProcessing.some(itemDataProcess => {
              return itemDataProcess.processing === watch('processing');
            });
          }
          if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
            return dataMap.dataProcessing.some(itemDataProcess => {
              return itemDataProcess.legalBase === watch('legalBase');
            });
          }
          if (watch('filter') === FILTER.POSITION && !!watch('position')) {
            return dataMap.dataProcessing.some(itemDataProcess => {
              return itemDataProcess.position === watch('position');
            });
          }
          return true;
        })
        .map(dataMap => {
          dataMap.dataProcessing = dataMap.dataProcessing.filter(dataProcess => {
            if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
              return dataProcess.processing === watch('processing');
            }
            if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
              return dataProcess.legalBase === watch('legalBase');
            }
            if (watch('filter') === FILTER.POSITION && !!watch('position')) {
              return dataProcess.position === watch('position');
            }
            return true;
          });
          return dataMap;
        });
    }
    return contentRopa;
  };

  const exportExcel = async () => {
    const response = await getExportFileRopa(getContentFiltered());
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    let fileName = '';
    switch (ropa.scope) {
      case 'Empresa':
        fileName = 'relatorio_ropa_versao_empresa.xlsx';
        break;
      case 'Área':
        fileName = 'relatorio_ropa_versao_area.xlsx';
        break;
      case 'Processo':
        fileName = 'relatorio_ropa_versao_processo.xlsx';
        break;
      default:
        fileName = 'relatorio_ropa_versao_sumario_executivo.xlsx';
    }
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {!isLoading && !error && (
        <Container title={`${ropa?.name}`} breadcrumb={`${t('dashboard:ropa.breadcrumb')} / ${ropa?.name}`} linkPage="/reports/ropa">
          <Box sx={{ width: '100%' }}>
            {/* Botão de exportar no topo, pequeno e alinhado à direita */}
            <Box sx={{ width: '8%', display: 'flex', justifyContent: 'flex-end', mb: 2, pr: 0, ml: '90%', mt: 2 }}>
              <Button
                variant="contained"
                sx={{
                  minWidth: 100,
                  height: 30,
                  px: 2,
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.80rem',
                  background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
                  boxShadow: '0 3px 10px rgba(46, 125, 50, 0.25)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                    boxShadow: '0 5px 14px rgba(46, 125, 50, 0.35)'
                  }
                }}
                aria-controls={anchorEl ? 'user-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={anchorEl ? 'true' : undefined}
                endIcon={<PrintOutlinedIcon fontSize="small" />}
                onClick={handleClick}
              >
                {t('common:buttons.export')}
              </Button>
            </Box>

            {/* Área de filtros e card de exportação removidos; botão agora na header */}
            <Menu
              anchorEl={anchorEl}
              id="user-menu"
              open={anchorEl != null}
              onClose={handleClose}
              onClick={handleClose}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    mt: 1.5,
                    minWidth: 220,
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <MenuItem
                  sx={{
                    borderRadius: '8px',
                    margin: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.08)',
                      transform: 'translateX(4px)'
                    }
                  }}
                  onClick={handlePrint}
                >
                <Avatar sx={{ bgcolor: red[50], mr: 2, width: 32, height: 32 }}>
                  <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: red[600] }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                    {t('dashboard:ropa.show.exportPdf')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {t('dashboard:ropa.show.exportPdfDesc')}
                  </Typography>
                </Box>
               </MenuItem>
               </motion.div>
               <Divider sx={{ mx: 2 }} />
               <motion.div
                 initial={{ opacity: 0, x: -20 }}
                 animate={{ opacity: 1, x: 0 }}
                 transition={{ duration: 0.3, delay: 0.2 }}
               >
                 <MenuItem
                   sx={{
                     borderRadius: '8px',
                     margin: '8px',
                     padding: '12px 16px',
                     transition: 'all 0.2s ease',
                     '&:hover': {
                       backgroundColor: 'rgba(76, 175, 80, 0.08)',
                       transform: 'translateX(4px)'
                     }
                   }}
                   onClick={exportExcel}
                 >
                <Avatar sx={{ bgcolor: green[50], mr: 2, width: 32, height: 32 }}>
                  <TableViewOutlinedIcon sx={{ fontSize: 18, color: green[600] }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                    {t('dashboard:ropa.show.exportExcel')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {t('dashboard:ropa.show.exportExcelDesc')}
                  </Typography>
                 </Box>
               </MenuItem>
               </motion.div>
             </Menu>
            <Box ref={componentRef}>
              {ropa.scope === 'Empresa' && (
                <ReportsRopaCompany
                  company={ropa.company}
                  filter={watch('filter')}
                  area={watch('area')}
                  process={watch('process')}
                  data={watch('data')}
                  category={watch('data')}
                  processing={watch('processing')}
                  legalBase={watch('legalBase')}
                  position={watch('position')}
                />
              )}
              {ropa.scope === 'Área' && (
                <ReportsRopaArea
                  area={ropa.area}
                  filter={watch('filter')}
                  process={watch('process')}
                  data={watch('data')}
                  category={watch('data')}
                  treatment={watch('treatment')}
                  legalBase={watch('legalBase')}
                  position={watch('position')}
                />
              )}
              {ropa.scope === 'Processo' && (
                <ReportsRopaProcess
                  process={ropa.process}
                  filter={watch('filter')}
                  data={watch('data')}
                  category={watch('category')}
                  treatment={watch('treatment')}
                  legalBase={watch('legalBase')}
                  position={watch('position')}
                />
              )}
              {ropa.scope === 'Sumário Executivo' && <ReportsRopaExecutiveSummary dados={ropa.executiveSummary} />}
            </Box>
          </Box>
        </Container>
      )}
      {isLoading && <PageLoaderShow />}
      {error && <div>{t('dashboard:ropa.show.error')}</div>}
    </>
  );
};
